import { Alert, Button, Paper, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { compactMoney, date, money } from '../../lib/format';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { AboutThisPage, PageHeader, StatCard, StatGrid, StatusChip } from '../../ui/primitives';

export default function CapitalReturnsPage() {
  const scope = useTenantScope();
  const navigate = useNavigate();
  const ready = scope.funds.filter(
    (fund) =>
      fund.id === 'f-brg2' &&
      fund.status !== 'WOUND_UP' &&
      !scope.capitalReturns.some((item) => item.fundId === fund.id && item.status !== 'REJECTED'),
  );
  const paid = scope.capitalReturns.filter((item) => item.status === 'PAID');
  return (
    <>
      <PageHeader
        title="Capital returns & wind-ups"
        subtitle="Return capital when a loan or asset repays, pay the final income, cancel units and close the trust."
        actions={
          <Button component={RouterLink} to="/manager/capital-returns/new" variant="contained">
            New capital return
          </Button>
        }
      />
      <AboutThisPage>
        <p>
          Most Fides trusts finish when the underlying loan repays. A capital return cancels units
          at the issue price and pays the money back with any final income. A final return winds up
          the trust: holders get an exit statement, the register for that trust closes, and its last
          tax statements and AIIR records follow at year end.
        </p>
        <p>
          Partial returns (for example, a staged repayment) cancel a percentage of every holding pro
          rata.
        </p>
      </AboutThisPage>
      {ready.map((fund) => (
        <Alert
          key={fund.id}
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              component={RouterLink}
              to={`/manager/capital-returns/new?fund=${fund.id}`}
              variant="contained"
              size="small"
            >
              Start wind-up
            </Button>
          }
        >
          <strong>{fund.name}</strong>: the borrower repaid in full on 14 October 2026. Capital is
          ready to return.
        </Alert>
      ))}
      <StatGrid>
        <StatCard
          label="Capital returned FY2027"
          value={compactMoney(
            sumBy(
              paid.flatMap((item) => item.lines),
              (line) => line.amount,
            ),
          )}
        />
        <StatCard
          label="Trusts wound up"
          value={scope.funds.filter((fund) => fund.status === 'WOUND_UP').length}
        />
        <StatCard
          label="In progress"
          value={
            scope.capitalReturns.filter((item) =>
              ['DRAFT', 'PENDING_APPROVAL', 'APPROVED'].includes(item.status),
            ).length
          }
          tone="warning"
        />
      </StatGrid>
      <Paper variant="outlined">
        <DataTable
          rows={scope.capitalReturns}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/manager/capital-returns/${row.id}`)}
          emptyMessage="No capital returns yet"
          columns={[
            {
              key: 'fund',
              label: 'Trust',
              render: (row) => (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {scope.fundById.get(row.fundId)?.name}
                </Typography>
              ),
            },
            {
              key: 'kind',
              label: 'Type',
              render: (row) =>
                row.kind === 'FINAL'
                  ? 'Final return and wind-up'
                  : `${row.returnPct}% partial return`,
            },
            { key: 'holders', label: 'Holders', align: 'right', render: (row) => row.lines.length },
            {
              key: 'capital',
              label: 'Capital',
              align: 'right',
              render: (row) => money(sumBy(row.lines, (line) => line.amount)),
            },
            {
              key: 'income',
              label: 'Final income',
              align: 'right',
              render: (row) => money(sumBy(row.lines, (line) => line.finalIncome)),
            },
            { key: 'date', label: 'Payment date', render: (row) => date(row.paymentDate) },
            { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
          ]}
        />
      </Paper>
    </>
  );
}
